import React, { createContext, useState, useContext, ReactNode } from 'react'
import { useLeads } from '../hooks/useLeads'

interface Lead {
  nome: string
  email: string
  whatsapp: string
}

interface Scores {
  terra: number
  agua: number
  fogo: number
  ar: number
}

interface ElementResult {
  score: number
  percentual: number
  classificacao: string
}

interface CalculatedResults {
  terraScore: number
  aguaScore: number
  fogoScore: number
  arScore: number
  terraPercentual: number
  aguaPercentual: number
  fogoPercentual: number
  arPercentual: number
  terraClassificacao: string
  aguaClassificacao: string
  fogoClassificacao: string
  arClassificacao: string
  elementosMaiorBloqueio: string[]
}

interface QuizContextType {
  lead: Lead | null
  scores: Scores
  resultado: string
  calculatedResults: CalculatedResults | null
  leadData: any
  isSubmitted: boolean
  interesseLeituraVibracional: string
  interesseProtocoloLiberacao: string
  setLead: (lead: Lead) => void
  updateScore: (element: 'terra' | 'agua' | 'fogo' | 'ar', increment: number) => void
  calculateResults: () => CalculatedResults
  calculateResult: (lastElement?: 'terra' | 'agua' | 'fogo' | 'ar', lastAnswer?: number, vibrationalInterest?: string, protocoloInterest?: string) => Promise<void>
  resetQuiz: () => void
  setInteresseLeituraVibracional: (value: string) => void
  setInteresseProtocoloLiberacao: (value: string) => void
}

const QuizContext = createContext<QuizContextType | undefined>(undefined)

export const QuizProvider = ({ children }: { children: ReactNode }) => {
  const [lead, setLead] = useState<Lead | null>(null)
  const [scores, setScores] = useState<Scores>({
    terra: 0,
    agua: 0,
    fogo: 0,
    ar: 0
  })
  const [resultado, setResultado] = useState<string>('')
  const [calculatedResults, setCalculatedResults] = useState<CalculatedResults | null>(null)
  const [leadData, setLeadData] = useState<any>(null)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [interesseLeituraVibracional, setInteresseLeituraVibracional] = useState<string>('')
  const [interesseProtocoloLiberacao, setInteresseProtocoloLiberacao] = useState<string>('')
  const { saveLead } = useLeads()

  const updateScore = (element: 'terra' | 'agua' | 'fogo' | 'ar', increment: number) => {
    setScores(prev => ({
      ...prev,
      [element]: prev[element] + increment
    }))
  }

  // Função que calcula todos os resultados
  const calculateResults = (lastElement?: 'terra' | 'agua' | 'fogo' | 'ar', lastAnswer?: number): CalculatedResults => {
    // Incluir a última resposta no cálculo
    const finalScores = { ...scores }
    if (lastElement && lastAnswer !== undefined) {
      finalScores[lastElement] = scores[lastElement] + lastAnswer
    }
    
    console.log('Scores atuais:', scores)
    console.log('Scores finais para cálculo:', finalScores)

    const terraScore = finalScores.terra
    const aguaScore = finalScores.agua
    const fogoScore = finalScores.fogo
    const arScore = finalScores.ar

    // Calcular percentuais: (score / 3) * 100
    const terraPercentual = Math.round((terraScore / 3) * 100)
    const aguaPercentual = Math.round((aguaScore / 3) * 100)
    const fogoPercentual = Math.round((fogoScore / 3) * 100)
    const arPercentual = Math.round((arScore / 3) * 100)

    // Função de classificação
    const getClassificacao = (percentual: number): string => {
      if (percentual === 0) return 'Equilibrado'
      if (percentual === 33) return 'Sensível'
      if (percentual === 67) return 'Atenção'
      if (percentual === 100) return 'Bloqueio Elevado'
      return 'Equilibrado'
    }

    const terraClassificacao = getClassificacao(terraPercentual)
    const aguaClassificacao = getClassificacao(aguaPercentual)
    const fogoClassificacao = getClassificacao(fogoPercentual)
    const arClassificacao = getClassificacao(arPercentual)

    // Identificar elemento(s) com maior bloqueio
    const maxPercentual = Math.max(terraPercentual, aguaPercentual, fogoPercentual, arPercentual)
    const elementosMaiorBloqueio: string[] = []

    if (maxPercentual > 0) {
      if (terraPercentual === maxPercentual) elementosMaiorBloqueio.push('terra')
      if (aguaPercentual === maxPercentual) elementosMaiorBloqueio.push('agua')
      if (fogoPercentual === maxPercentual) elementosMaiorBloqueio.push('fogo')
      if (arPercentual === maxPercentual) elementosMaiorBloqueio.push('ar')
    }

    return {
      terraScore,
      aguaScore,
      fogoScore,
      arScore,
      terraPercentual,
      aguaPercentual,
      fogoPercentual,
      arPercentual,
      terraClassificacao,
      aguaClassificacao,
      fogoClassificacao,
      arClassificacao,
      elementosMaiorBloqueio
    }
  }

  const calculateResult = async (lastElement?: 'terra' | 'agua' | 'fogo' | 'ar', lastAnswer?: number, vibrationalInterest?: string, protocoloInterest?: string) => {
    // Verificar se já foi enviado
    if (isSubmitted) {
      return
    }

    const results = calculateResults(lastElement, lastAnswer)
    
    // Definir resultado principal
    if (results.elementosMaiorBloqueio.length === 0) {
      setResultado('equilibrado')
    } else {
      setResultado(results.elementosMaiorBloqueio[0])
    }
    
    setCalculatedResults(results)

    // Salvar lead no banco de dados
    if (lead) {
      try {
        const savedLead = await saveLead({
          nome: lead.nome,
          email: lead.email,
          whatsapp: lead.whatsapp,
          terraScore: results.terraScore,
          aguaScore: results.aguaScore,
          fogoScore: results.fogoScore,
          arScore: results.arScore,
          terraPercentual: results.terraPercentual,
          aguaPercentual: results.aguaPercentual,
          fogoPercentual: results.fogoPercentual,
          arPercentual: results.arPercentual,
          terraClassificacao: results.terraClassificacao,
          aguaClassificacao: results.aguaClassificacao,
          fogoClassificacao: results.fogoClassificacao,
          arClassificacao: results.arClassificacao,
          elementosMaiorBloqueio: results.elementosMaiorBloqueio,
          interesseLeituraVibracional: vibrationalInterest || interesseLeituraVibracional,
          interesseProtocoloLiberacao: protocoloInterest || interesseProtocoloLiberacao
        })
        setLeadData(savedLead)
        setIsSubmitted(true)
        console.log('Lead salvo no banco de dados com sucesso')
      } catch (error) {
        console.error('Erro ao salvar lead:', error)
      }
    }
  }

  const resetQuiz = () => {
    setLead(null)
    setScores({ terra: 0, agua: 0, fogo: 0, ar: 0 })
    setResultado('')
    setCalculatedResults(null)
    setLeadData(null)
    setIsSubmitted(false)
    setInteresseLeituraVibracional('')
    setInteresseProtocoloLiberacao('')
  }

  return (
    <QuizContext.Provider value={{
      lead,
      scores,
      resultado,
      calculatedResults,
      leadData,
      isSubmitted,
      interesseLeituraVibracional,
      interesseProtocoloLiberacao,
      setLead,
      updateScore,
      calculateResults,
      calculateResult,
      resetQuiz,
      setInteresseLeituraVibracional,
      setInteresseProtocoloLiberacao
    }}>
      {children}
    </QuizContext.Provider>
  )
}

export const useQuiz = () => {
  const context = useContext(QuizContext)
  if (!context) {
    throw new Error('useQuiz must be used within a QuizProvider')
  }
  return context
}
